
export const kbCategories = [
  { id: 'all', label: 'All Topics', icon: 'ri-apps-line' },
  { id: 'specs', label: 'Technical Specs', icon: 'ri-cpu-line' },
  { id: 'setup', label: 'Setup Guides', icon: 'ri-tools-line' },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: 'ri-bug-line' },
  { id: 'best-practices', label: 'Best Practices', icon: 'ri-star-line' },
];

export const kbArticles = [
  // TECHNICAL SPECS
  {
    id: 'spec-headset-pro',
    category: 'specs',
    icon: 'ri-headphone-line',
    title: 'Silent Headset Pro — Full Technical Specifications',
    summary: 'Complete specs for our flagship wireless headset used in corporate conferences and multilingual events.',
    readTime: '5 min',
    tags: ['headset', 'wireless', 'audio'],
    sections: [
      {
        heading: 'Audio Performance',
        items: [
          { label: 'Frequency Response', value: '20 Hz – 20,000 Hz (±3 dB)' },
          { label: 'Signal-to-Noise Ratio', value: '≥ 90 dB' },
          { label: 'Total Harmonic Distortion', value: '< 0.1% at 1 kHz' },
          { label: 'Maximum SPL', value: '110 dB' },
          { label: 'Driver Size', value: '40 mm neodymium' },
          { label: 'Impedance', value: '32 Ω' },
        ],
      },
      {
        heading: 'Wireless Connectivity',
        items: [
          { label: 'Transmission Technology', value: 'RF 2.4 GHz / 863–865 MHz' },
          { label: 'Operating Range', value: 'Up to 100 m (open field)' },
          { label: 'Simultaneous Channels', value: 'Up to 3 independent channels' },
          { label: 'Latency', value: '< 5 ms' },
          { label: 'Encryption', value: 'AES-128 bit' },
        ],
      },
      {
        heading: 'Battery & Physical',
        items: [
          { label: 'Battery Life', value: 'Up to 10 hours continuous use' },
          { label: 'Charging Time', value: '2 hours (USB-C)' },
          { label: 'Weight', value: '185 g' },
          { label: 'Connector', value: 'USB-C (charging) + 3.5 mm jack (wired fallback)' },
          { label: 'Operating Temperature', value: '0°C to 45°C' },
          { label: 'Certifications', value: 'CE, FCC, ANATEL' },
        ],
      },
    ],
  },
  {
    id: 'spec-transmitter',
    category: 'specs',
    icon: 'ri-broadcast-line',
    title: 'RF Transmitter Station — Technical Specifications',
    summary: 'Detailed specs for the base transmitter unit that powers all silent conference channels simultaneously.',
    readTime: '4 min',
    tags: ['transmitter', 'RF', 'channels'],
    sections: [
      {
        heading: 'Transmission',
        items: [
          { label: 'Frequency Band', value: '863–865 MHz (EU) / 2.4 GHz (ISM)' },
          { label: 'Output Power', value: '10 mW (EU compliant)' },
          { label: 'Modulation', value: 'FM / Digital OFDM' },
          { label: 'Channels Available', value: '3 simultaneous (expandable to 6)' },
          { label: 'Audio Input', value: 'XLR balanced + 3.5 mm aux' },
          { label: 'Input Impedance', value: '10 kΩ balanced' },
        ],
      },
      {
        heading: 'Power & Physical',
        items: [
          { label: 'Power Supply', value: '100–240 V AC, 50/60 Hz' },
          { label: 'Power Consumption', value: '15 W max' },
          { label: 'Dimensions', value: '220 × 140 × 45 mm (1U rack)' },
          { label: 'Weight', value: '1.2 kg' },
          { label: 'Operating Temperature', value: '-10°C to 50°C' },
          { label: 'Antenna', value: 'Dual omnidirectional (included)' },
        ],
      },
    ],
  },
  {
    id: 'spec-charging-case',
    category: 'specs',
    icon: 'ri-battery-charge-line',
    title: 'Charging & Transport Case — Specifications',
    summary: 'Specs for the smart charging case that stores and charges up to 50 headsets simultaneously.',
    readTime: '3 min',
    tags: ['charging', 'transport', 'logistics'],
    sections: [
      {
        heading: 'Charging System',
        items: [
          { label: 'Capacity', value: '50 headsets per case' },
          { label: 'Charging Standard', value: 'USB-C 5V/2A per slot' },
          { label: 'Total Charge Time', value: '2.5 hours (all 50 units)' },
          { label: 'Power Input', value: '220 V AC (standard Brazilian outlet)' },
          { label: 'Status Indicators', value: 'LED per slot (red = charging, green = full)' },
        ],
      },
      {
        heading: 'Physical & Transport',
        items: [
          { label: 'Case Material', value: 'ABS hard shell + foam interior' },
          { label: 'Dimensions (closed)', value: '600 × 400 × 300 mm' },
          { label: 'Weight (loaded)', value: '18 kg' },
          { label: 'Wheels', value: '4 × 360° swivel wheels' },
          { label: 'Lock', value: 'TSA-approved combination lock' },
        ],
      },
    ],
  },

  // SETUP GUIDES
  {
    id: 'setup-basic-event',
    category: 'setup',
    icon: 'ri-settings-3-line',
    title: 'Basic Event Setup — Step-by-Step Guide',
    summary: 'How to set up a single-channel silent conference for up to 200 attendees in under 30 minutes.',
    readTime: '8 min',
    tags: ['setup', 'beginner', 'single-channel'],
    steps: [
      {
        number: 1,
        title: 'Site Survey & Positioning',
        description: 'Arrive at least 60 minutes before the event. Walk the venue to identify potential RF interference sources (Wi-Fi routers, other wireless systems). Place the transmitter centrally at a height of 1.5–2 m for maximum coverage.',
        tip: 'Avoid placing the transmitter near metal structures or inside enclosed cabinets.',
      },
      {
        number: 2,
        title: 'Transmitter Setup',
        description: 'Connect the transmitter to a 220 V outlet. Attach both antennas firmly. Connect your audio source (microphone mixer or laptop) via XLR or 3.5 mm jack. Set the input gain so the LED meter peaks at -6 dB.',
        tip: 'Always use a balanced XLR connection when available to minimize noise.',
      },
      {
        number: 3,
        title: 'Channel Configuration',
        description: 'Press and hold the CHANNEL button for 3 seconds to enter setup mode. Select Channel 1 (green LED). Confirm the frequency is set to 863.1 MHz. Press CONFIRM to save.',
        tip: 'For single-language events, Channel 1 is always the default.',
      },
      {
        number: 4,
        title: 'Headset Distribution & Pairing',
        description: 'Remove headsets from the charging case. Each headset auto-pairs to the last saved channel. Power on by holding the side button for 2 seconds. The LED will flash green when connected.',
        tip: 'Pre-pair all headsets to the correct channel before distributing to attendees.',
      },
      {
        number: 5,
        title: 'Audio Level Check',
        description: 'Put on a headset and speak into the microphone. Adjust the volume wheel on the headset to a comfortable level. Ask a colleague to test from the far end of the venue to confirm range.',
        tip: 'Recommended listening level: 70–80 dB SPL for speech clarity without fatigue.',
      },
      {
        number: 6,
        title: 'Attendee Briefing',
        description: 'Instruct attendees to: power on the headset (hold side button 2 sec), select the correct channel using the +/- buttons, and return the headset to the case after the event.',
        tip: 'Print a simple QR code card with these instructions for self-service.',
      },
    ],
  },
  {
    id: 'setup-multilingual',
    category: 'setup',
    icon: 'ri-translate-2',
    title: 'Multilingual Event Setup — 3-Channel Configuration',
    summary: 'Configure simultaneous interpretation across 3 languages for international conferences and summits.',
    readTime: '10 min',
    tags: ['multilingual', 'interpretation', 'advanced'],
    steps: [
      {
        number: 1,
        title: 'Plan Your Channel Map',
        description: 'Assign each language to a channel before arrival. Example: Channel 1 = Portuguese (original), Channel 2 = English, Channel 3 = Spanish. Document this map and share with interpreters.',
        tip: 'Always keep the original language on Channel 1 for attendees who do not need interpretation.',
      },
      {
        number: 2,
        title: 'Interpreter Booth Setup',
        description: 'Connect each interpreter\'s microphone to a separate input on the mixer. Route each mixer output to the corresponding transmitter input channel. Label all cables clearly.',
        tip: 'Use color-coded cable ties matching your channel map colors.',
      },
      {
        number: 3,
        title: 'Transmitter Multi-Channel Mode',
        description: 'Enter setup mode on the transmitter. Enable all 3 channels simultaneously. Verify each channel shows a solid LED (not blinking). Set individual gain levels per channel.',
        tip: 'Run a 5-minute test with all interpreters speaking simultaneously to check for crosstalk.',
      },
      {
        number: 4,
        title: 'Headset Channel Assignment',
        description: 'Distribute headsets pre-set to the attendee\'s preferred language. Use colored stickers on headsets matching the channel map. Attendees can still switch channels manually using +/- buttons.',
        tip: 'For VIP attendees, pre-configure their headset and label it with their name.',
      },
      {
        number: 5,
        title: 'Interpreter Monitoring',
        description: 'Each interpreter should wear a headset on their own channel to monitor their output. Provide a dedicated monitor headset for the event coordinator to check all channels.',
        tip: 'Schedule a 15-minute rehearsal with interpreters before doors open.',
      },
    ],
  },
  {
    id: 'setup-outdoor',
    category: 'setup',
    icon: 'ri-sun-line',
    title: 'Outdoor Event Setup — Extended Range Configuration',
    summary: 'Special considerations and antenna placement for outdoor events, festivals, and open-air conferences.',
    readTime: '7 min',
    tags: ['outdoor', 'range', 'antenna'],
    steps: [
      {
        number: 1,
        title: 'RF Environment Assessment',
        description: 'Use a spectrum analyzer app to scan for interference in the 863–865 MHz band. Outdoor environments often have less interference than indoor venues but are subject to weather and physical obstacles.',
        tip: 'Scan at the same time of day as your event — RF conditions change throughout the day.',
      },
      {
        number: 2,
        title: 'Antenna Placement for Range',
        description: 'Mount directional antennas on tripods at 3 m height. Point them toward the audience area. For areas larger than 5,000 m², use antenna splitters to add 2 additional remote antennas.',
        tip: 'Maintain line-of-sight between antennas and the audience whenever possible.',
      },
      {
        number: 3,
        title: 'Weather Protection',
        description: 'Place the transmitter unit inside a weatherproof IP54-rated enclosure. Use cable glands for all connections. Avoid direct sunlight on the transmitter — operating temperature must stay below 45°C.',
        tip: 'Bring a portable canopy or tent to shade the equipment station.',
      },
      {
        number: 4,
        title: 'Power Supply Planning',
        description: 'Use a UPS (uninterruptible power supply) for the transmitter to handle generator fluctuations. Ensure the generator output is stable at 220 V ±5%. Use surge protectors on all equipment.',
        tip: 'Always have a backup power source for events longer than 4 hours.',
      },
    ],
  },

  // TROUBLESHOOTING
  {
    id: 'trouble-no-audio',
    category: 'troubleshooting',
    icon: 'ri-volume-mute-line',
    title: 'No Audio on Headset — Diagnosis & Fix',
    summary: 'Step-by-step troubleshooting when attendees report no sound or very low volume on their headsets.',
    readTime: '5 min',
    tags: ['no audio', 'silent', 'fix'],
    issues: [
      {
        problem: 'Headset LED is off',
        cause: 'Battery depleted or headset not powered on',
        solution: 'Hold the power button for 2 seconds. If LED does not light up, place headset in charging case for 15 minutes before retrying.',
      },
      {
        problem: 'LED is red (solid)',
        cause: 'Battery below 10% — low battery warning',
        solution: 'Swap with a fully charged headset from the reserve stock. Always keep 10% of headsets as charged spares.',
      },
      {
        problem: 'LED is green but no audio',
        cause: 'Wrong channel selected or transmitter not broadcasting',
        solution: 'Press the + button to cycle through channels. Check that the transmitter LED for the selected channel is solid (not blinking). Verify audio source is connected and playing.',
      },
      {
        problem: 'Audio is very low / barely audible',
        cause: 'Volume wheel at minimum or input gain too low on transmitter',
        solution: 'Turn the volume wheel on the headset clockwise to maximum. On the transmitter, increase the input gain until the meter peaks at -6 dB.',
      },
      {
        problem: 'Intermittent audio / cutting out',
        cause: 'RF interference or attendee is out of range',
        solution: 'Move closer to the transmitter. Check for new interference sources (phones, other wireless devices). Try switching to an alternate channel.',
      },
    ],
  },
  {
    id: 'trouble-pairing',
    category: 'troubleshooting',
    icon: 'ri-link-unlink',
    title: 'Headset Pairing Issues — Complete Guide',
    summary: 'Resolve pairing failures, channel mismatches, and factory reset procedures.',
    readTime: '4 min',
    tags: ['pairing', 'channel', 'reset'],
    issues: [
      {
        problem: 'Headset LED flashing rapidly (red/green)',
        cause: 'Headset is in pairing mode but cannot find transmitter',
        solution: 'Ensure the transmitter is powered on and broadcasting. Hold the headset PAIR button for 5 seconds until LED turns solid green.',
      },
      {
        problem: 'Headset paired to wrong channel',
        cause: 'Previously saved channel configuration',
        solution: 'Use the +/- buttons to manually select the correct channel. To permanently save: hold the CHANNEL button for 3 seconds while on the desired channel.',
      },
      {
        problem: 'Factory reset required',
        cause: 'Persistent pairing or configuration issues',
        solution: 'Hold POWER + CHANNEL buttons simultaneously for 8 seconds. LED will flash 3 times white to confirm reset. Re-pair to the transmitter from scratch.',
      },
    ],
  },

  // BEST PRACTICES
  {
    id: 'bp-hygiene',
    category: 'best-practices',
    icon: 'ri-shield-check-line',
    title: 'Headset Hygiene & Sanitization Protocol',
    summary: 'Recommended cleaning procedures between events to maintain hygiene standards for shared headsets.',
    readTime: '4 min',
    tags: ['hygiene', 'cleaning', 'protocol'],
    practices: [
      {
        title: 'After Every Event',
        items: [
          'Wipe ear cushions with 70% isopropyl alcohol wipes',
          'Allow 5 minutes drying time before placing in charging case',
          'Inspect ear cushions for damage — replace if torn or compressed',
          'Clean headband with a damp microfiber cloth',
        ],
      },
      {
        title: 'Weekly Deep Clean',
        items: [
          'Remove ear cushions and wash with mild soap and water',
          'Dry completely (24 hours) before reattaching',
          'Clean charging contacts with a dry cotton swab',
          'Check all cables and connectors for wear',
        ],
      },
      {
        title: 'Disposable Covers (Recommended for Public Events)',
        items: [
          'Use single-use hygienic covers for each attendee',
          'Covers are available in packs of 100 — contact us to order',
          'Reduces cleaning time by 70% at large events',
          'Attendees appreciate the hygiene assurance',
        ],
      },
    ],
  },
  {
    id: 'bp-event-planning',
    category: 'best-practices',
    icon: 'ri-calendar-check-line',
    title: 'Event Planning Checklist — Silent Conference',
    summary: 'Complete pre-event, during-event, and post-event checklist for flawless silent conference execution.',
    readTime: '6 min',
    tags: ['checklist', 'planning', 'event'],
    practices: [
      {
        title: '48 Hours Before',
        items: [
          'Confirm headset count (add 10% buffer for spares)',
          'Charge all headsets to 100% — verify via charging case LEDs',
          'Test transmitter with audio source in office',
          'Confirm venue power availability (220 V, dedicated circuit)',
          'Brief all technical staff on channel map and emergency procedures',
        ],
      },
      {
        title: 'Day of Event — Setup (T-60 min)',
        items: [
          'Arrive 60 minutes before doors open',
          'Complete site survey and transmitter placement',
          'Run full audio test on all channels',
          'Distribute headsets to registration desk',
          'Brief registration staff on headset distribution procedure',
          'Confirm spare headsets are charged and accessible',
        ],
      },
      {
        title: 'During Event',
        items: [
          'Monitor transmitter LEDs every 15 minutes',
          'Keep 10% spare headsets at a service station',
          'Have a technician available for immediate support',
          'Log any issues for post-event review',
        ],
      },
      {
        title: 'Post-Event',
        items: [
          'Collect all headsets — count against distribution log',
          'Immediately charge all returned headsets',
          'Sanitize all headsets before storage',
          'Complete equipment condition report',
          'Return equipment to transport cases within 2 hours',
        ],
      },
    ],
  },
];
